import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Upload, CheckCircle, Loader2, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubmitCVPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    requestSubject: '',
    recruitmentRole: '',
    positionSought: [],
    amfPermit: '',
    formationType: '',
    freeMessage: '',
    message: '',
    cvFile: null,
    consent: false,
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 5 * 1024 * 1024) {
      setFormData((prev) => ({ ...prev, cvFile: file }));
    } else {
      toast.error('Le fichier doit faire moins de 5 Mo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.requestSubject || !formData.consent) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.requestSubject === 'Recrutement' && (!formData.positionSought.length || !formData.amfPermit || !formData.cvFile)) {
      toast.error('Veuillez remplir tous les champs obligatoires pour le recrutement');
      return;
    }

    setIsSubmitting(true);

    try {
      const data = new FormData();
      data.append('fullName', formData.fullName);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('city', formData.city);
      data.append('requestSubject', formData.requestSubject);

      if (formData.requestSubject === 'Recrutement') {
        data.append('positionSought', formData.positionSought.join(', '));
        data.append('amfPermit', formData.amfPermit);
        data.append('message', formData.message);
        data.append('cvFile', formData.cvFile);
      } else if (formData.requestSubject === 'Formation') {
        data.append('positionSought', 'N/A');
        data.append('amfPermit', 'N/A');
        data.append('message', `Type de formation désirée : ${formData.formationType}`);
        // Create a dummy file for the backend
        const blob = new Blob(['Aucun CV - demande de formation'], { type: 'text/plain' });
        data.append('cvFile', blob, 'demande-formation.txt');
      } else {
        data.append('positionSought', 'N/A');
        data.append('amfPermit', 'N/A');
        data.append('message', formData.freeMessage);
        const blob = new Blob(['Aucun CV - autre demande'], { type: 'text/plain' });
        data.append('cvFile', blob, 'autre-demande.txt');
      }

      const response = await fetch(`${API_URL}/api/submit-cv`, {
        method: 'POST',
        body: data,
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur lors de la soumission');
      }

      toast.success('Demande envoyée avec succès!');
      setIsSubmitted(true);
    } catch (error) {
      toast.error(error.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="pt-16 min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <Card className="border-0 shadow-2xl">
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 mb-8">
                <CheckCircle className="w-16 h-16 text-green-600" />
              </div>
              <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                Merci pour votre confiance !
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Votre demande a bien été reçue.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Notre équipe l'examinera avec attention et vous contactera dans les meilleurs délais.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-[#1a1a1a] overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6">
            Nous contacter
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            Une question, une candidature ou un besoin en formation ? Écrivez-nous.
          </p>
        </div>
      </section>

      {/* Nexi & Élyor - Indicateur */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src="/images/mascots/indicateur.png"
            alt="Nexi et Élyor vous invitent à remplir le formulaire"
            className="w-full max-w-lg mx-auto"
          />
        </div>
      </section>

      {/* Form */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Objet de la demande — FIRST */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-gold pb-2">
                    Objet de la demande
                  </h3>
                  <div>
                    <Label htmlFor="requestSubject" className="text-base">Pourquoi nous contactez-vous ? <span className="text-red-500">*</span></Label>
                    <select
                      id="requestSubject"
                      name="requestSubject"
                      value={formData.requestSubject}
                      onChange={handleInputChange}
                      required
                      data-testid="request-subject-select"
                      className="mt-2 h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Sélectionnez un objet</option>
                      <option value="Recrutement">Recrutement</option>
                      <option value="Formation">Formation</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </div>

                {/* CONDITIONAL: Recrutement — sub-choice */}
                {formData.requestSubject === 'Recrutement' && (
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-gold pb-2">
                      Vous êtes...
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, recruitmentRole: 'candidat' }))}
                        data-testid="role-candidat"
                        className={`p-6 rounded-xl border-2 text-left transition-all ${
                          formData.recruitmentRole === 'candidat'
                            ? 'border-gold bg-gold/5'
                            : 'border-gray-200 hover:border-gold/50'
                        }`}
                      >
                        <p className="text-lg font-semibold text-gray-900">Je suis un candidat</p>
                        <p className="text-sm text-gray-500 mt-1">Je souhaite soumettre ma candidature</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, recruitmentRole: 'employeur' }))}
                        data-testid="role-employeur"
                        className={`p-6 rounded-xl border-2 text-left transition-all ${
                          formData.recruitmentRole === 'employeur'
                            ? 'border-gold bg-gold/5'
                            : 'border-gray-200 hover:border-gold/50'
                        }`}
                      >
                        <p className="text-lg font-semibold text-gray-900">Je suis un employeur</p>
                        <p className="text-sm text-gray-500 mt-1">Je cherche des talents pour mon équipe</p>
                      </button>
                    </div>
                  </div>
                )}

                {/* Employer message */}
                {formData.requestSubject === 'Recrutement' && formData.recruitmentRole === 'employeur' && (
                  <div className="p-8 bg-[#1a1a1a] rounded-xl border border-gold/30" data-testid="employer-message">
                    <p className="text-white text-lg leading-relaxed mb-6">
                      Besoin de talents pour votre équipe ? Discutons-en de vive voix. Contactez-nous à{' '}
                      <a href="mailto:info@nexortalents.com" className="text-gold font-semibold hover:underline">info@nexortalents.com</a>{' '}
                      ou par téléphone pour un service personnalisé et rapide.
                    </p>
                    <a href="tel:5145453383" className="inline-flex items-center gap-2 px-5 py-3 bg-gold hover:bg-[#9a7109] text-white font-semibold rounded-lg transition-colors">
                      <Phone className="w-4 h-4" />
                      514-545-3383
                    </a>
                  </div>
                )}

                {/* Informations personnelles — only when NOT employer */}
                {formData.requestSubject && !(formData.requestSubject === 'Recrutement' && formData.recruitmentRole === 'employeur') && !(formData.requestSubject === 'Recrutement' && !formData.recruitmentRole) && (
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-gold pb-2">
                      Informations personnelles
                    </h3>
                    <div className="space-y-5">
                      <div>
                        <Label htmlFor="fullName" className="text-base">Nom complet <span className="text-red-500">*</span></Label>
                        <Input id="fullName" name="fullName" value={formData.fullName} onChange={handleInputChange} required className="mt-2 h-12" />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-base">Adresse courriel <span className="text-red-500">*</span></Label>
                        <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required className="mt-2 h-12" />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-base">Numéro de téléphone</Label>
                        <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} className="mt-2 h-12" />
                      </div>
                      <div>
                        <Label htmlFor="city" className="text-base">Ville / Région</Label>
                        <Input id="city" name="city" value={formData.city} onChange={handleInputChange} className="mt-2 h-12" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Candidat: full recruitment form */}
                {formData.requestSubject === 'Recrutement' && formData.recruitmentRole === 'candidat' && (
                  <>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-gold pb-2">
                        Objectifs professionnels
                      </h3>
                      <div className="space-y-5">
                        <div>
                          <Label className="text-base">Quel(s) type(s) de poste recherchez-vous ? <span className="text-red-500">*</span></Label>
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="position-checkboxes">
                            {['Souscripteur', 'Courtier / Agent', 'Gestionnaire de compte', 'Analyste', 'Expert en sinistre', 'Formateur', 'Développement des affaires', 'Directeur'].map((position) => (
                              <label
                                key={position}
                                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  formData.positionSought.includes(position)
                                    ? 'border-gold bg-gold/5'
                                    : 'border-gray-200 hover:border-gold/50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.positionSought.includes(position)}
                                  onChange={() => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      positionSought: prev.positionSought.includes(position)
                                        ? prev.positionSought.filter((p) => p !== position)
                                        : [...prev.positionSought, position]
                                    }));
                                  }}
                                  className="w-4 h-4 accent-[#b8860b]"
                                />
                                <span className="text-sm text-gray-700">{position}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="amfPermit" className="text-base">Permis de l'AMF ? <span className="text-red-500">*</span></Label>
                          <select
                            id="amfPermit"
                            name="amfPermit"
                            value={formData.amfPermit}
                            onChange={handleInputChange}
                            required
                            className="mt-2 h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            <option value="">Sélectionnez</option>
                            <option value="Oui">Oui</option>
                            <option value="Non">Non</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="message" className="text-base">Message ou présentation personnelle</Label>
                          <Textarea id="message" name="message" value={formData.message} onChange={handleInputChange} placeholder="Quelques lignes sur vos aspirations, votre parcours ou ce que vous recherchez" rows={5} className="mt-2" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-gold pb-2">
                        Téléversement de CV
                      </h3>
                      <div>
                        <Label htmlFor="cvFile" className="text-base">Joindre votre CV <span className="text-red-500">*</span></Label>
                        <p className="text-sm text-gray-500 mb-3 mt-1">Formats acceptés : PDF, Word — max 5 Mo</p>
                        <Input
                          id="cvFile"
                          name="cvFile"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          required
                          className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gold file:text-white hover:file:bg-[#9a7109] cursor-pointer"
                        />
                        {formData.cvFile && (
                          <p className="text-sm text-green-600 mt-3 flex items-center">
                            <Upload className="w-4 h-4 mr-2" />
                            {formData.cvFile.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* CONDITIONAL: Formation */}
                {formData.requestSubject === 'Formation' && (
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-gold pb-2">
                      Formation
                    </h3>
                    <div>
                      <Label htmlFor="formationType" className="text-base">Type de formation désirée</Label>
                      <Input
                        id="formationType"
                        name="formationType"
                        value={formData.formationType}
                        onChange={handleInputChange}
                        placeholder="Ex. : déontologie, coaching entrevue, gestion de notes..."
                        className="mt-2 h-12"
                      />
                    </div>
                  </div>
                )}

                {/* CONDITIONAL: Autre */}
                {formData.requestSubject === 'Autre' && (
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6 border-b-2 border-gold pb-2">
                      Votre demande
                    </h3>
                    <div>
                      <Label htmlFor="freeMessage" className="text-base">Décrivez votre demande</Label>
                      <Textarea
                        id="freeMessage"
                        name="freeMessage"
                        value={formData.freeMessage}
                        onChange={handleInputChange}
                        placeholder="Décrivez votre demande ou votre besoin..."
                        rows={5}
                        className="mt-2"
                      />
                    </div>
                  </div>
                )}

                {/* Consent + Submit — visible when subject is selected, NOT for employer */}
                {formData.requestSubject && !(formData.requestSubject === 'Recrutement' && (formData.recruitmentRole === 'employeur' || !formData.recruitmentRole)) && (
                  <>
                    <div className="flex items-start space-x-3 pt-4">
                      <Checkbox
                        id="consent"
                        checked={formData.consent}
                        onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, consent: checked }))}
                        required
                        className="mt-1"
                      />
                      <Label htmlFor="consent" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                        Je consens à ce que mes informations soient utilisées par Nexor Talents dans le cadre de cette demande, conformément à la politique de confidentialité. <span className="text-red-500">*</span>
                      </Label>
                    </div>

                    <div className="pt-6">
                      <Button type="submit" disabled={isSubmitting} className="w-full bg-gold hover:bg-[#9a7109] text-white text-lg py-7 font-semibold disabled:opacity-70">
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Envoi en cours...
                          </>
                        ) : (
                          'Envoyer ma demande'
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Confidentiality */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">
            Confidentialité assurée
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Votre parcours mérite le plus grand respect. Chez Nexor Talents, vos informations sont traitées avec discrétion, professionnalisme et bienveillance.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SubmitCVPage;
